import { useState } from "react";
import {
  App,
  Button,
  Empty,
  Flex,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import {
  BotTaskInterface,
  useDeleteBotTaskMutation,
  useGetBotTasksQuery,
  useToggleBotTaskMutation,
} from "api";
import { dateTimeFormat, format } from "libs";
import { getErrorMessage, Put } from "components";
import { AddTaskModal } from "./components/AddTaskModal";

// Mirrors BotTaskService::MAX_TASKS_PER_BOT (3) on the server. When the cap
// changes there it must be bumped here too — server enforces, this just gates
// the UI to avoid a guaranteed 422 response.
const MAX_TASKS_PER_BOT = 3;

interface BotTasksTabProps {
  botId: number;
}

export const BotTasksTab = ({ botId }: BotTasksTabProps) => {
  const { notification, modal } = App.useApp();
  const { data, isLoading, refetch } = useGetBotTasksQuery({ botId });

  const [toggleTask] = useToggleBotTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteBotTaskMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTask, setEditTask] = useState<BotTaskInterface | undefined>();

  const onDelete = (task: BotTaskInterface) => {
    modal.confirm({
      title: `Sterge task "${task.task_type.label}"?`,
      content:
        "Logul actiunilor istorice ramane (cu task_id devenit NULL). Decizia este IREVERSIBILA.",
      okText: "Sterge",
      okButtonProps: { danger: true, loading: isDeleting },
      onOk: async () => {
        try {
          await deleteTask({ id: task.id, botId }).unwrap();
          notification.success({ message: "Task sters." });
        } catch (e) {
          notification.error({ message: getErrorMessage(e as never) });
        }
      },
    });
  };

  const onToggle = async (task: BotTaskInterface) => {
    try {
      await toggleTask({ id: task.id, botId }).unwrap();
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  const columns: ColumnsType<BotTaskInterface> = [
    {
      title: "Tip",
      dataIndex: ["task_type", "label"],
      render: (label: string) => <Tag>{label}</Tag>,
    },
    {
      title: "Config",
      dataIndex: "config",
      render: (config: Record<string, unknown>) => (
        <Typography.Text style={{ fontFamily: "monospace", fontSize: 12 }}>
          {summariseConfig(config)}
        </Typography.Text>
      ),
    },
    {
      title: "Programare",
      dataIndex: "schedule",
      render: (schedule: BotTaskInterface["schedule"]) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {schedule.active_hours_start}–{schedule.active_hours_end} ·{" "}
          {schedule.timezone}
          {" · "}weekend ×{schedule.weekend_modifier}
        </Typography.Text>
      ),
    },
    {
      title: "Prio",
      dataIndex: "priority",
      align: "center" as const,
      width: 60,
    },
    {
      title: "Activ",
      dataIndex: "is_enabled",
      align: "center" as const,
      width: 80,
      render: (_, task) => (
        <Switch
          checked={task.is_enabled}
          onChange={() => onToggle(task)}
          size="small"
        />
      ),
    },
    {
      title: "Urmatoarea rulare",
      dataIndex: "next_run_at",
      render: (next: string | null) => (
        <Put>{next ? format(next, dateTimeFormat) : null}</Put>
      ),
    },
    {
      title: "Ultima rulare",
      dataIndex: "last_run_at",
      render: (last: string | null) => (
        <Put>{last ? format(last, dateTimeFormat) : null}</Put>
      ),
    },
    {
      title: "",
      align: "right" as const,
      width: 100,
      render: (_, task) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditTask(task);
              setModalOpen(true);
            }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(task)}
          />
        </Space>
      ),
    },
  ];

  const taskCount = data?.data.length ?? 0;
  const atLimit = taskCount >= MAX_TASKS_PER_BOT;

  const addButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      disabled={atLimit}
      onClick={() => {
        setEditTask(undefined);
        setModalOpen(true);
      }}
    >
      Adauga task
    </Button>
  );

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: "1rem" }}>
        <Space size="middle">
          <Typography.Text type="secondary">
            Sarcini configurate. Worker-ul polleaza si executa pe baza
            urmatoarei rulari.
          </Typography.Text>
          <Tag color={atLimit ? "warning" : "default"}>
            {taskCount}/{MAX_TASKS_PER_BOT} sarcini
          </Tag>
        </Space>
        {atLimit ? (
          <Tooltip title={`Maxim ${MAX_TASKS_PER_BOT} sarcini per bot. Sterge una existenta sau creeaza un bot nou.`}>
            {/* Span wrapper so Tooltip works on disabled Button (Antd quirk) */}
            <span>{addButton}</span>
          </Tooltip>
        ) : (
          addButton
        )}
      </Flex>

      <Table<BotTaskInterface>
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading}
        rowKey="id"
        pagination={false}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <Empty description="Niciun task configurat. Adauga primul." />
          ),
        }}
      />

      <AddTaskModal
        open={modalOpen}
        botId={botId}
        task={editTask}
        usedTaskTypes={(data?.data ?? []).map((t) => t.task_type.value)}
        onCancel={() => {
          setModalOpen(false);
          setEditTask(undefined);
        }}
        onSaved={() => refetch()}
      />
    </>
  );
};

function summariseConfig(config: Record<string, unknown>): string {
  const entries = Object.entries(config)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => {
      if (Array.isArray(v)) return `${k}=[${v.length}]`;
      return `${k}=${String(v)}`;
    });
  return entries.join(" · ") || "—";
}
